import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Smartphone, AlertTriangle, Copy, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';

interface MFAStatus {
  enabled: boolean;
  pending?: boolean;
  method?: string;
  backupCodesRemaining?: number;
}

interface MFASetupData {
  secret: string;
  qrCodeData: string;
  manualEntryKey: string;
}

function MFA() {
  const { t } = useTranslation();
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBackupCodesOpen, setIsBackupCodesOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);
  const [setupData, setSetupData] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartMFASetup = async () => {
    setIsSettingUp(true);
    setSetupError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/setup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSetupData(data.data);
        setIsSetupDialogOpen(true);
        setVerificationCode('');
      } else {
        setSetupError(data.error || 'Failed to setup MFA');
      }
    } catch (error) {
      console.error('Failed to setup MFA:', error);
      setSetupError('Failed to setup MFA');
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleVerifyAndEnableMFA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setSetupError('Please enter a valid 6-digit code');
      return;
    }

    setIsVerifying(true);
    setSetupError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/enable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: verificationCode }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setIsSetupDialogOpen(false);
        setSetupData(null);
        setVerificationCode('');
        if (data.data?.backupCodes) {
          setBackupCodes(data.data.backupCodes);
          setIsBackupCodesOpen(true);
        }
        fetchMFAStatus();
      } else {
        setSetupError(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Failed to verify MFA:', error);
      setSetupError('Failed to verify MFA');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopySecret = () => {
    if (setupData?.manualEntryKey) {
      navigator.clipboard.writeText(setupData.manualEntryKey.replace(/\s/g, ''));
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const handleCloseSetupDialog = () => {
    setIsSetupDialogOpen(false);
    setSetupData(null);
    setVerificationCode('');
    setSetupError(null);
  };

  const handleDisableMFA = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/disable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchMFAStatus();
      }
    } catch (error) {
      console.error('Failed to disable MFA:', error);
    }
  };

  const handleGenerateBackupCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/me/mfa/backup-codes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.data?.codes || []);
        setIsBackupCodesOpen(true);
        fetchMFAStatus();
      }
    } catch (error) {
      console.error('Failed to generate backup codes:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Security"
        icon={ShieldCheck}
        title={t('mfa.title')}
        subtitle={t('mfa.subtitle')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('mfa.statusCard.title')}</CardTitle>
          <CardDescription>{t('mfa.statusCard.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaStatus ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('mfa.statusCard.status')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('mfa.statusCard.mfaIs')} {mfaStatus.enabled ? t('mfa.statusCard.enabled') : t('mfa.statusCard.disabled')}
                  </p>
                </div>
                <StatusBadge status={mfaStatus.enabled ? 'ENABLED' : 'DISABLED'} />
              </div>

              {mfaStatus.enabled && (
                <>
                  {mfaStatus.method && (
                    <div>
                      <p className="font-medium">{t('mfa.statusCard.method')}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        {mfaStatus.method}
                      </p>
                    </div>
                  )}

                  {mfaStatus.backupCodesRemaining !== undefined && (
                    <div>
                      <p className="font-medium">{t('mfa.statusCard.backupCodes')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('mfa.statusCard.backupCodesRemaining', { count: mfaStatus.backupCodesRemaining })}
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="flex flex-wrap gap-4">
                {mfaStatus.enabled ? (
                  <>
                    <Button variant="outline" onClick={handleDisableMFA}>
                      {t('mfa.button.disable')}
                    </Button>
                    <Button variant="outline" onClick={handleGenerateBackupCodes}>{t('mfa.button.generateBackupCodes')}</Button>
                  </>
                ) : (
                  <Button onClick={handleStartMFASetup} disabled={isSettingUp}>
                    {isSettingUp ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('mfa.button.enable')
                    )}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground">{t('mfa.statusCard.noMfaStatus')}</p>
          )}
        </CardContent>
      </Card>

      {!mfaStatus?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle>{t('mfa.howItWorks.title')}</CardTitle>
            <CardDescription>{t('mfa.howItWorks.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('mfa.howItWorks.info')}
            </p>
            <div className="space-y-2">
              <h4 className="font-medium">{t('mfa.howItWorks.benefits')}</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>{t('mfa.howItWorks.benefit1')}</li>
                <li>{t('mfa.howItWorks.benefit2')}</li>
                <li>{t('mfa.howItWorks.benefit3')}</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MFA Setup Dialog */}
      <Dialog open={isSetupDialogOpen} onOpenChange={handleCloseSetupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('mfa.dialogs.setup.title')}</DialogTitle>
            <DialogDescription>
              {t('mfa.dialogs.setup.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {setupData && (
              <>
                <div className="flex justify-center">
                  <div className="p-4 bg-white rounded-lg">
                    <QRCodeSVG value={setupData.qrCodeData} size={200} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">
                    {t('mfa.dialogs.setup.manualEntry')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                      {setupData.manualEntryKey}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopySecret}
                      title={t('common.copy')}
                    >
                      {copiedSecret ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="verification-code">
                    {t('mfa.dialogs.setup.enterCode')}
                  </Label>
                  <Input
                    id="verification-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setVerificationCode(value);
                      setSetupError(null);
                    }}
                    className="text-center text-lg tracking-widest font-mono"
                  />
                </div>
                {setupError && (
                  <div className="flex items-center gap-2 text-sm text-red-500">
                    <AlertTriangle className="h-4 w-4" />
                    {setupError}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCloseSetupDialog}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleVerifyAndEnableMFA}
              disabled={isVerifying || verificationCode.length !== 6}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('mfa.dialogs.setup.verify')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={isBackupCodesOpen} onOpenChange={setIsBackupCodesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('mfa.dialogs.backupCodes.title')}</DialogTitle>
            <DialogDescription>
              {t('mfa.dialogs.backupCodes.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <p className="font-semibold mb-1">{t('mfa.dialogs.backupCodes.warningTitle')}</p>
                <p>{t('mfa.dialogs.backupCodes.warningMessage')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="p-2 bg-muted rounded font-mono text-sm text-center"
                >
                  {code}
                </div>
              ))}
            </div>
            {backupCodes.length === 0 && (
              <p className="text-center text-muted-foreground text-sm">
                {t('mfa.dialogs.backupCodes.noCodes')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setIsBackupCodesOpen(false)}>
              {t('mfa.dialogs.backupCodes.savedCodes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MFA;
